import { Box, Flex } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { TableProvider } from 'components/common/table/context';
import { PitchPerfControls } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/controls';
import { PitchPerfGroupTable } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/group-table';
import { PitchPerfMetrics } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/metrics';
import { PitchPerfPlots } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/plots';
import { PitchPerfShotTableHoC } from 'components/sections/admin-portal/machines/dialogs/pitch-performance/shot-table';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import {
  CheckedContext,
  CheckedProvider,
} from 'contexts/layout/checked.context';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { useContext, useEffect, useMemo, useRef } from 'react';

const COMPONENT_NAME = 'PitchPerformance';

interface IProps {
  machine: IMachine;
}

export const PitchPerformanceHoC = (props: IProps) => {
  const groups = usePitchPerformanceStore((s) => s.groups);

  // we need to wrap everything in a table and checked provider
  // so that we can use the table to control the charts + metrics

  return (
    <ErrorBoundary identifier="PitchPerformanceHoC">
      <TableProvider identifier="PitchPerformanceTableProvider">
        <CheckedProvider data={groups}>
          <PitchPerformance {...props} />
        </CheckedProvider>
      </TableProvider>
    </ErrorBoundary>
  );
};

const PitchPerformance = (props: IProps) => {
  const filters = usePitchPerformanceStore((s) => s.filters);
  const groups = usePitchPerformanceStore((s) => s.groups);
  const refGroups = usePitchPerformanceStore((s) => s.refGroups);
  const firstGroups = usePitchPerformanceStore((s) => s.firstGroups);
  const visibleGroups = usePitchPerformanceStore((s) => s.visibleGroups);
  const machineID = usePitchPerformanceStore((s) => s.machineID);
  const loading = usePitchPerformanceStore((s) => s.loading);
  const selectedPitchId = usePitchPerformanceStore((s) => s.selectedPitchId);
  const selectedMsHash = usePitchPerformanceStore((s) => s.selectedMsHash);

  const { tally, checkedIDs, setChecked, resetChecked, getChecked } =
    useContext(CheckedContext);

  // array of pitch IDs checked-off at time of drill down
  const checkedGroups = useRef<string[]>([]);

  // determines what is summarized for metrics
  useEffect(() => {
    const selectedGroup = groups.find((g) => g._id === selectedPitchId);

    if (!selectedGroup) {
      // top level view w/o any pitch selected => show all current MS groups, filtered by checked status
      const currentMsGroups = groups
        .filter((g) => g.isCurrentMs) // for 6.10, top level only shows current MS
        .filter((g) => tally.checked === 0 || getChecked(g._id));

      console.debug(
        `${COMPONENT_NAME}: setting visible groups based isCurrentMs and checked status`,
        currentMsGroups
      );

      usePitchPerformanceStore.setState({
        visibleGroups: currentMsGroups,
      });
      return;
    }

    const pitchGroups = groups.filter(
      (g) => g.pitch.bs_hash === selectedGroup.pitch.bs_hash
    );

    if (!selectedMsHash) {
      // drill down view w/o any ms hash selected => show all groups for the selected pitch
      console.debug(
        `${COMPONENT_NAME}: setting visible groups based on bs_hash`,
        pitchGroups
      );

      usePitchPerformanceStore.setState({
        visibleGroups: pitchGroups,
      });
      return;
    }

    // drill down view w/ a specific ms hash selected => show all groups for the selected pitch that match the ms hash
    const msGroups = pitchGroups.filter(
      (g) => g.target.ms.matching_hash === selectedMsHash
    );

    console.debug(
      `${COMPONENT_NAME}: setting visible groups based on matching_hash`,
      msGroups
    );

    usePitchPerformanceStore.setState({
      visibleGroups: msGroups,
    });
  }, [groups, tally, selectedPitchId, selectedMsHash, getChecked]);

  useEffect(() => {
    usePitchPerformanceStore.getState().setFirstGroups(visibleGroups);
  }, [visibleGroups]);

  useEffect(() => {
    usePitchPerformanceStore.getState().fetchStatsPitches(firstGroups, true);
  }, [firstGroups]);

  useEffect(() => {}, []);

  const prevGroups = useMemo(() => {
    if (tally.checked === 0) {
      // nothing checked => everything is visible
      return refGroups;
    }

    return refGroups.filter((g) => getChecked(g._id));
  }, [refGroups, tally.key, tally.checked]);

  useEffect(() => {
    usePitchPerformanceStore
      .getState()
      .initialize(props.machine.machineID, props.machine.ball_type);

    return () => usePitchPerformanceStore.getState().reset();
  }, [props.machine.ball_type, props.machine.machineID]);

  // re-generate report whenever filters change
  useEffect(() => {
    if (machineID) {
      resetChecked();
      usePitchPerformanceStore.setState({
        selectedPitchId: '',
        selectedMsHash: '',
      });
      usePitchPerformanceStore.getState().generate();
    }
  }, [machineID, filters]);

  const onDrillDown = (id: string) => {
    // keep track of what groups were checked off before drilling down so we can restore them after drilling back up
    checkedGroups.current = checkedIDs;

    usePitchPerformanceStore.setState({ selectedPitchId: id });

    // setting checked here will show only the drilled down pitch in the break/plate plots
    resetChecked();
    setChecked([id], true);
  };

  const onDrillup = () => {
    // restore the groups that were checked off before drilling down
    resetChecked();
    setChecked(checkedGroups.current, true);
  };

  const plotGroups = useMemo(() => {
    if (!selectedMsHash) {
      // this will already be filtered by isCurrentMs, checked status, etc...
      return visibleGroups;
    }

    return visibleGroups.filter(
      (g) => g.target.ms.matching_hash === selectedMsHash
    );
  }, [visibleGroups, selectedMsHash]);

  return (
    <ErrorBoundary identifier="PitchPerformance">
      <Flex gap="5" direction="column">
        <PitchPerfControls machine={props.machine} />

        <PitchPerfMetrics
          groups={plotGroups}
          refGroups={prevGroups}
          loading={loading}
        />

        <PitchPerfPlots groups={visibleGroups} refGroups={undefined} />

        {/* keep tables in the DOM to persist any internal state they're maintaining (e.g. search queries) */}
        <Box className={!selectedPitchId ? 'hidden' : ''}>
          <PitchPerfShotTableHoC onDrillUp={onDrillup} />
        </Box>

        <Box className={selectedPitchId ? 'hidden' : ''}>
          <PitchPerfGroupTable
            machine={props.machine}
            onDrillDown={onDrillDown}
          />
        </Box>
      </Flex>
    </ErrorBoundary>
  );
};
